// fonts event
import { faGrid as iconLaunchpadMain } from "@fortawesome/pro-thin-svg-icons";
import { faCalendarDay as iconDatesMain } from "@fortawesome/pro-thin-svg-icons";
import { faMapLocation as iconLocationMain } from "@fortawesome/pro-thin-svg-icons";
import { faRocketLaunch as iconEntryMain } from "@fortawesome/pro-thin-svg-icons";
import { faMessage as iconMessagingMain } from "@fortawesome/pro-thin-svg-icons";
import { faShare as iconSharingMain } from "@fortawesome/pro-thin-svg-icons";
import { faCreditCardAlt as iconTransactMain } from "@fortawesome/pro-thin-svg-icons";
import { faUser as iconMembersMain } from "@fortawesome/pro-thin-svg-icons";
import { faBarcodeRead as iconTicketsMain } from "@fortawesome/pro-thin-svg-icons";
import { faImage as iconGallery } from "@fortawesome/pro-thin-svg-icons";
import { faStore as iconProductsMain } from "@fortawesome/pro-thin-svg-icons";
import { faInfoCircle as iconInfoMain } from "@fortawesome/pro-thin-svg-icons";
import { faRocketLaunch as iconLaunchpadEdit } from "@fortawesome/pro-thin-svg-icons";
import { faCalendarPen as iconDatesEdit } from "@fortawesome/pro-thin-svg-icons";
import { faMapLocation as iconLocationEdit } from "@fortawesome/pro-thin-svg-icons";
import { faCommentPen as iconMessagingEdit } from "@fortawesome/pro-thin-svg-icons";
import { faScannerKeyboard as iconEntryEdit } from "@fortawesome/pro-thin-svg-icons";
import { faShare as iconSharingEdit } from "@fortawesome/pro-thin-svg-icons";
import { faCreditCardAlt as iconTransactEdit } from "@fortawesome/pro-thin-svg-icons";
import { faUser as iconMembersEdit } from "@fortawesome/pro-thin-svg-icons";
import { faNfcPen as iconTicketsEdit } from "@fortawesome/pro-thin-svg-icons";
import { faGalleryThumbnails as iconGalleryEdit } from "@fortawesome/pro-thin-svg-icons";
import { faStore as iconProductsEdit } from "@fortawesome/pro-thin-svg-icons";
import { faInfoCircle as iconInfoEdit } from "@fortawesome/pro-thin-svg-icons";
import { faLock as iconDisabled } from "@fortawesome/pro-thin-svg-icons";
import { faClose as iconClose } from "@fortawesome/pro-thin-svg-icons";
import { faGrid as iconMenu } from "@fortawesome/pro-thin-svg-icons";
import { fa1 as icon1 } from "@fortawesome/pro-thin-svg-icons";
import { fa2 as icon2 } from "@fortawesome/pro-thin-svg-icons";
import { fa3 as icon3 } from "@fortawesome/pro-thin-svg-icons";
import { fa4 as icon4 } from "@fortawesome/pro-thin-svg-icons";
import { fa5 as icon5 } from "@fortawesome/pro-thin-svg-icons";
import { fa6 as icon6 } from "@fortawesome/pro-thin-svg-icons";

/**
 * Event Module Item Object Definition
 * @typedef {Object} EventModuleItem
 * @property {string} id - The unique identifier for the menu item
 * @property {string} name - The display name of the menu item
 * @property {string} shortName - A shorter name for the menu item
 * @property {string} shortSubtext - Additional short text for the menu item
 * @property {Object} tipText - Tooltip text for different user types
 * @property {string} tipText.runners - Tooltip for runners
 * @property {string} tipText.riders - Tooltip for riders
 * @property {string} description - A description of the menu item
 * @property {string} fallback - Fallback text if content is unavailable
 * @property {string} info - Additional information about the menu item
 * @property {string} help - Help text for the menu item
 * @property {string} float - The float position of the menu item (e.g., "left", "right")
 * @property {boolean} active - Whether the menu item is active
 * @property {boolean} bookend - Whether the menu item is a bookend (start or end of menu)
 * @property {boolean} secondaryNav - Whether the menu item belongs in secondary navigation
 * @property {boolean} visitingRider - Whether the menu item is visible to visiting riders
 * @property {Object} counterparts - The corresponding names for different user types
 * @property {string} counterparts.runners - Counterpart name for runners
 * @property {string} counterparts.riders - Counterpart name for riders
 * @property {Object} icon - Icons for different states of the menu item
 * @property {Object} icon.main - The main icon for the menu item
 * @property {Object} icon.edit - The icon used in edit mode
 * @property {Object} icon.close - The icon used for closing or exiting
 * @property {Object} icon.disabled - The icon used when the menu item is disabled
 * @property {boolean} noHeaderActions - Whether to hide header actions for this menu item
 * @property {boolean} noHeaderCounters - Whether to hide header counters for this menu item
 * @property {boolean} noHeaderDivider - Whether to hide the header divider for this menu item
 * @property {boolean} actionExit - Whether this menu item is an exit action
 */

/**
 * Create Event Module Item Object
 * @param {Object} params
 * @param {string} params.id
 * @param {string} params.name
 * @param {string} params.shortName
 * @param {string} params.shortSubtext
 * @param {Object} params.tipText
 * @param {string} params.tipText.runners
 * @param {string} params.tipText.riders
 * @param {string} params.description
 * @param {string} params.fallback
 * @param {string} params.info
 * @param {string} params.help
 * @param {string} params.float
 * @param {boolean} params.active
 * @param {boolean} params.bookend
 * @param {boolean} params.secondaryNav
 * @param {boolean} params.visitingRider
 * @param {Object} params.counterparts
 * @param {string} params.counterparts.runners
 * @param {string} params.counterparts.riders
 * @param {Object} params.icon
 * @param {Object} params.icon.main
 * @param {Object} params.icon.edit
 * @param {Object} params.icon.close
 * @param {Object} params.icon.disabled
 * @param {boolean} params.noHeaderActions
 * @param {boolean} params.noHeaderCounters
 * @param {boolean} params.noHeaderDivider
 * @param {boolean} params.actionExit
 * @returns {EventModuleItem}
 */
export function createEventModuleItem({
  id,
  name,
  shortName,
  shortSubtext,
  tipText,
  description,
  fallback,
  info,
  help,
  float,
  active,
  bookend,
  secondaryNav,
  visitingRider,
  counterparts,
  icon,
  noHeaderActions = false,
  noHeaderCounters = false,
  noHeaderDivider = false,
  actionExit = false,
}) {
  return {
    id,
    name,
    shortName,
    shortSubtext,
    tipText,
    description,
    fallback,
    info,
    help,
    float,
    active,
    bookend,
    secondaryNav,
    visitingRider,
    counterparts,
    icon: {
      main: icon.main || null,
      edit: icon.edit || null,
      close: icon.close || null,
      disabled: icon.disabled || null,
    },
    noHeaderActions,
    noHeaderCounters,
    noHeaderDivider,
    actionExit,
  };
}

/**
 * Event Module List Definition
 * @typedef {EventModuleItem[]} EventModuleList
 */

/**
 * Create Event Module List
 * @returns {EventModuleList} The created Event Module List Object
 */
export function createEventModuleList() {
  return [
    createEventModuleItem({
      id: "menu",
      name: "Follow the Eventuator Brick Road",
      shortName: "Menu",
      shortSubtext: "Main Menu. Where to?",
      tipText: {
        runners: "Return to eventuator home",
        riders: "Return to eventuator home",
      },
      active: false,
      bookend: true,
      secondaryNav: true,
      visitingRider: true,
      float: "left",
      counterparts: {
        runners: "Menu",
        riders: "Menu",
      },
      description: "Main Menu",
      fallback: "No menu items",
      icon: {
        main: iconMenu,
        edit: iconMenu,
        close: iconClose,
        disabled: iconDisabled,
      },
    }),

    createEventModuleItem({
      id: "launchpad",
      name: "Prepare for Launch",
      shortName: "Prepare",
      shortSubtext: "Start",
      tipText: {
        runners: "Start with launch settings",
        riders: "Start with launch details",
      },
      active: true,
      visitingRider: true,
      counterparts: {
        runners: "Criteria",
        riders: "Criteria",
      },
      description: "Launch Criteria",
      fallback: "No launch criteria yet",
      info: "Runners post what's happening and set launch criteria, such as minimum riders and setup notice. No fixed dates yet. Riders review and check out what happens on launch.",
      help: "Check out what's happening and review launch criteria",
      icon: {
        main: iconLaunchpadMain,
        number: icon1,
        edit: iconLaunchpadEdit,
        close: iconClose,
        disabled: iconDisabled,
      },
      noHeaderActions: true,
      noHeaderCounters: true,
      noHeaderDivider: true,
    }),
    createEventModuleItem({
      id: "sharing",
      name: "Share and Promote",
      shortName: "Share",
      shortSubtext: "Promote",
      tipText: {
        runners: "Go to flyer room",
        riders: "Go to flyer room",
      },
      active: true,
      visitingRider: true,
      counterparts: {
        runners: "Flyers",
        riders: "Flyers",
      },
      description: "Promotion Links",
      fallback: "No shares yet",
      info: "Both runners and riders share and promote the eventuator around the web to help discover demand and drive dates towards launch.",
      help: "Promote and share the event link to generate interest",
      icon: {
        main: iconSharingMain,
        number: icon2,
        edit: iconSharingEdit,
        close: iconClose,
        disabled: iconDisabled,
      },
    }),
    createEventModuleItem({
      id: "dates",
      name: "Calendar Exchange",
      shortName: "Compare",
      shortSubtext: "Dates",
      tipText: {
        runners: "Go to calendar room",
        riders: "Go to calendar room",
      },
      active: true,
      visitingRider: true,
      counterparts: {
        runners: "Offers",
        riders: "Offers",
      },
      description: "Date Offer Exchange",
      fallback: "No offers yet",
      info: "Everyone exchanges dates. Runners offer run dates. Riders offer ticket dates. Dates trigger when demand matches launch criteria. Tickets and payment are exchanged only if dates launch.",
      help: "Everyone exchanges dates. Runners & Riders place offers and dates tick up towards launch...",
      icon: {
        main: iconDatesMain,
        number: icon3,
        edit: iconDatesEdit,
        close: iconClose,
        disabled: iconDisabled,
      },
    }),
    createEventModuleItem({
      id: "locations",
      name: "Locate the Launch Site",
      shortName: "Locate",
      shortSubtext: "Site",
      tipText: {
        runners: "Go to map room",
        riders: "Go to map room",
      },
      active: true,
      visitingRider: true,
      counterparts: {
        runners: "Places",
        riders: "Places",
      },
      description: "Where Launches Happen",
      fallback: "No places yet",
      info: "Use maps to exchange ideas on potential launch locations. Riders can make suggestions before runners finalize where it's all happening.",
      help: "Exchange locations and confirm the launch site",
      icon: {
        main: iconLocationMain,
        number: icon4,
        edit: iconLocationEdit,
        close: iconClose,
        disabled: iconDisabled,
      },
    }),
    createEventModuleItem({
      id: "messaging",
      name: "Chat with Everyone",
      shortName: "Communicate",
      shortSubtext: "Chat",
      tipText: {
        runners: "Go to chat rooms",
        riders: "Go to chat rooms",
      },
      active: true,
      visitingRider: true,
      counterparts: {
        runners: "Posts",
        riders: "Posts",
      },
      description: "Message Board",
      fallback: "No messages yet",
      info: "Plan, chat and debate with everyone in the event via group messaging. Runners and riders can communicate before, during and after dates launch.",
      help: "Chat with everyone to validate plans",
      icon: {
        main: iconMessagingMain,
        number: icon5,
        edit: iconMessagingEdit,
        close: iconClose,
        disabled: iconDisabled,
      },
    }),
    createEventModuleItem({
      id: "entry",
      name: "Matched Dates",
      shortName: "Eventuate",
      shortSubtext: "Ignition",
      tipText: {
        runners: "Go to ignition room",
        riders: "Go to ignition room",
      },
      active: true,
      visitingRider: true,
      counterparts: {
        runners: "Matched",
        riders: "Matched",
      },
      description: "Matched Date Launches",
      fallback: "Entry not available",
      info: "Launch! When dates trigger all matching offers are executed and everyone is notified. Riders are issued tickets. Runners receive their guest lists.",
      help: "Dates matched? Launch! Runners & Riders get together. Rinse and repeat...",
      icon: {
        main: iconEntryMain,
        number: icon6,
        edit: iconEntryEdit,
        close: iconClose,
        disabled: iconDisabled,
      },
    }),

    // secondary modules
    createEventModuleItem({
      id: "tickets",
      name: "Offer Types",
      shortName: "Tickets",
      shortSubtext: "Ticket Offer Types",
      tipText: {
        runners: "View ticket offer types",
        riders: "View ticket offer types",
      },
      active: true,
      secondaryNav: true,
      visitingRider: true,
      counterparts: {
        runners: "Offer Types",
        riders: "Offer Types",
      },
      description: "Offer Types for Riders",
      fallback: "No offer types yet",
      icon: {
        main: iconTicketsMain,
        edit: iconTicketsEdit,
        close: iconClose,
        disabled: iconDisabled,
      },
    }),
    createEventModuleItem({
      id: "gallery",
      name: "Image Gallery",
      shortName: "Gallery",
      shortSubtext: "Media",
      tipText: {
        runners: "View eventuator media",
        riders: "View eventuator media",
      },
      active: true,
      secondaryNav: true,
      visitingRider: true,
      counterparts: {
        runners: "Media Items",
        riders: "Media Items",
      },
      description: "Media Gallery",
      fallback: "No gallery items yet",
      icon: {
        main: iconGallery,
        edit: iconGalleryEdit,
        close: iconClose,
        disabled: iconDisabled,
      },
    }),
    createEventModuleItem({
      id: "members",
      name: "Eventuator Members",
      shortName: "Members",
      shortSubtext: "Members",
      tipText: {
        runners: "Runner members",
        riders: "Rider members",
      },
      active: true,
      secondaryNav: true,
      visitingRider: true,
      counterparts: {
        runners: "Members",
        riders: "Members",
      },
      description: "Members of this eventuator",
      fallback: "No members yet",
      icon: {
        main: iconMembersMain,
        edit: iconMembersEdit,
        close: iconClose,
        disabled: iconDisabled,
      },
    }),
    createEventModuleItem({
      id: "store",
      name: "Eventuator Store",
      shortName: "Store",
      shortSubtext: "Product Store",
      tipText: {
        runners: "List products for sale",
        riders: "Purchase products",
      },
      active: true,
      secondaryNav: true,
      visitingRider: true,
      float: "left",
      counterparts: {
        runners: "Products",
        riders: "Products",
      },
      description: "Browse and purchase products",
      fallback: "No products available",
      icon: {
        main: iconProductsMain,
        edit: iconProductsEdit,
        close: iconClose,
        disabled: iconDisabled,
      },
    }),
    createEventModuleItem({
      id: "transact",
      name: "Direct Payments",
      shortName: "Transact",
      shortSubtext: "Direct Pay",
      tipText: {
        runners: "Sell products & direct payments",
        riders: "Make direct payments",
      },
      active: false,
      secondaryNav: true,
      float: "left",
      counterparts: {
        runners: "Sales",
        riders: "Purchases",
      },
      description: "Make Direct Payments",
      fallback: "Direct pay unavailable",
      icon: {
        main: iconTransactMain,
        edit: iconTransactEdit,
        close: iconClose,
        disabled: iconDisabled,
      },
    }),
    createEventModuleItem({
      id: "info",
      name: "Other Information",
      shortName: "Info",
      shortSubtext: "Other Info",
      tipText: {
        runners: "View other information",
        riders: "View other information",
      },
      active: true,
      secondaryNav: true,
      float: "left",
      counterparts: {
        runners: "Items",
        riders: "Items",
      },
      description: "Other information about the eventuator",
      fallback: "No information available",
      icon: {
        main: iconInfoMain,
        edit: iconInfoEdit,
        close: iconClose,
        disabled: iconDisabled,
      },
    }),
    createEventModuleItem({
      id: "exit",
      name: "Exit Event",
      shortName: "Exit",
      shortSubtext: "Head Out",
      tipText: {
        runners: "Exit the eventuator",
        riders: "Exit the eventuator",
      },
      active: true,
      bookend: true,
      secondaryNav: true,
      visitingRider: true,
      float: "right",
      actionExit: true,
      counterparts: {
        runners: "Exit",
        riders: "Exit",
      },
      description: "Exit and return to events",
      fallback: "Exit unavailable",
      icon: {
        main: iconClose,
        edit: iconClose,
        close: iconClose,
        disabled: iconDisabled,
      },
    }),
  ];
}

/**
 * Event Dynamic Module Item Object Definition
 * @typedef {Object} EventDynamicModuleItem
 * @property {string} id - The unique identifier of the module, used for tracking updates
 * @property {boolean} active - Whether the module is active or not
 * @property {boolean} open - Whether the module is open or not
 * @property {Object} text - The text content for the module
 * @property {Object} text.runner - The text content for the runner role
 * @property {string} text.runner.title - The title for the runner role
 * @property {string} text.runner.description - The description for the runner role
 * @property {string} text.runner.action - The action text for the runner role
 * @property {Object} text.rider - The text content for the rider role
 * @property {string} text.rider.title - The title for the rider role
 * @property {string} text.rider.description - The description for the rider role
 * @property {string} text.rider.action - The action text for the rider role
 * @property {Object} counters - The counters for the module
 * @property {Object} counters.runner - The counters for the runner role
 * @property {number} counters.runner.header - The header counter for the runner role
 * @property {Object} counters.runner.viewer - The viewer counters for the runner role
 * @property {number} counters.runner.viewer.primary - The primary viewer counter for the runner role
 * @property {number} counters.runner.viewer.secondary - The secondary viewer counter for the runner role
 * @property {Object} counters.rider - The counters for the rider role
 * @property {number} counters.rider.header - The header counter for the rider role
 * @property {Object} counters.rider.viewer - The viewer counters for the rider role
 * @property {number} counters.rider.viewer.primary - The primary viewer counter for the rider role
 * @property {number} counters.rider.viewer.secondary - The secondary viewer counter for the rider role
 */

/**
 * Create Event Dynamic Module Item Object
 * @param {Object} params
 * @param {string} params.id
 * @param {boolean} params.active
 * @param {boolean} params.open
 * @param {Object} params.text
 * @param {Object} params.text.runner
 * @param {string} params.text.runner.title
 * @param {string} params.text.runner.description
 * @param {string} params.text.runner.action
 * @param {Object} params.text.rider
 * @param {string} params.text.rider.title
 * @param {string} params.text.rider.description
 * @param {string} params.text.rider.action
 * @param {Object} params.counters
 * @param {Object} params.counters.runner
 * @param {number} params.counters.runner.header
 * @param {Object} params.counters.runner.viewer
 * @param {number} params.counters.runner.viewer.primary
 * @param {number} params.counters.runner.viewer.secondary
 * @param {Object} params.counters.rider
 * @param {number} params.counters.rider.header
 * @param {Object} params.counters.rider.viewer
 * @param {number} params.counters.rider.viewer.primary
 * @param {number} params.counters.rider.viewer.secondary
 * @returns {EventDynamicModuleItem}
 */
export function createEventDynamicModuleItem({
  id = "default_module",
  active = false,
  open = false,
  text = {
    runner: {
      title: "Default Runner Module",
      description: "Description for default runner module",
      action: "Runner Action",
    },
    rider: {
      title: "Default Rider Module",
      description: "Description for default rider module",
      action: "Rider Action",
    },
  },
  counters = {
    runner: {
      header: 0,
      viewer: {
        primary: 0,
        secondary: 0,
      },
    },
    rider: {
      header: 0,
      viewer: {
        primary: 0,
        secondary: 0,
      },
    },
  },
}) {
  return {
    id,
    active,
    open,
    text,
    counters,
  };
}

/**
 * Event Dynamic Module List Definition
 * @typedef {EventDynamicModuleItem[]} EventDynamicModuleList
 */

/**
 * Create Event Dynamic Module List
 * @returns {EventDynamicModuleList}
 */
export function createEventDynamicModuleList() {
  const moduleList = createEventModuleList();
  const dynamicModuleList = moduleList.map((module) =>
    createEventDynamicModuleItem({
      id: module.id,
      active: module.active,
    }),
  );
  return dynamicModuleList;
}

/**
 * Event Dynamic Module Map Definition
 * @typedef {Object.<string, EventDynamicModuleItem>} EventDynamicModuleMap
 */

/**
 * Create Event Dynamic Module Map
 * @returns {EventDynamicModuleMap}
 */
export function createEventDynamicModuleMap() {
  const moduleList = createEventModuleList();
  const dynamicModuleMap = {};
  moduleList.forEach((module) => {
    dynamicModuleMap[module.id] = createEventDynamicModuleItem({
      id: module.id,
      active: module.active,
    });
  });
  return dynamicModuleMap;
}
